import React from "react";
import { LoaderFunctionArgs, Navigate, Route } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "./store/store";
import axios from "axios";

import UnprotectedRoute from "./views/common/global/UnprotectedRoute";
import ProtectedRoute from "./views/common/global/ProtectedRoute";

import {
  BuyerSignIn as BuyerSignInView,
  BuyerSignUp as BuyerSignUpView,
  BuyerHome as BuyerHomeView,
  PropertyDetails as PropertyDetailsView,
  PropertyPurchase as PropertyPurchaseView,
  PurchaseInvoice as PurchaseInvoiceView,
  PurchaseCheckout as PurchaseCheckoutView,
  PurchaseAccountSetup as PurchaseAccountSetupView,
  PropertySearch as PropertySearchView,
  BuyerDashboard as BuyerDashboardView,
  TermsAndConditions as TermsAndConditionsView,
  BuyerProfile as BuyerProfileView,
  HowItWorks as HowItWorksView,
  PrivacyPolicy as PrivacyPolicyView,
  BuyerTransactions as BuyerTransactionsView,
  BuyerSavedItems as BuyerSavedItemsView,
  BuyerProperties as BuyerPropertiesView,
  About as AboutView,
  RefundPolicy as RefundPolicyView,
  ForgotPassword as ForgotPasswordView,
  Faq as FaqView,
  ContactUs as ContactUsView,
  DataConsent as DataConsentView,
  OurTeam as OurTeamView,
  Blogs as BlogsView,
  Blog as BlogView,
} from "./views";
import apiRoutes from "./views/common/utils/apiRoutes";
import { ListingInterface } from "./types";
import { USER_TYPES } from "./views/common/utils/constants";
import LoggedOutRoute from "./views/common/global/LoggedOutRoute";

const BuyerRoutes = () => {
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.user
  );

  const listingLoader = async ({
    params,
  }: LoaderFunctionArgs): Promise<ListingInterface> => {
    const listingId = params.listingId;
    const response = await axios.get(
      `${apiRoutes.buyerListing.view}/${listingId}`
    );
    const data = response.data.data;
    return data;
  };
  let dashboardUrl = "";

  if (user && "user-types" in user) {
    // @ts-ignore
    const userType = user["user-types"][0].type;
    if (userType === "buyer") dashboardUrl = "/dashboard/my-properties";
    else if (userType === "seller") dashboardUrl = "/seller/dashboard";
    else dashboardUrl = "/admin/dashboard";
  }

  return (
    <>
      <Route
        path="/"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <BuyerHomeView />
          </UnprotectedRoute>
        }
      />
      <Route
        path={`/sign-in`}
        element={
          <LoggedOutRoute
            isAuthenticated={isAuthenticated}
            redirectUrl={dashboardUrl}
          >
            <BuyerSignInView />
          </LoggedOutRoute>
        }
      />
      <Route
        path={`/sign-up`}
        element={
          <LoggedOutRoute
            isAuthenticated={isAuthenticated}
            redirectUrl={dashboardUrl}
          >
            <BuyerSignUpView />
          </LoggedOutRoute>
        }
      />
      <Route
        path="/reset-password/:id/:key"
        element={
          <LoggedOutRoute
            isAuthenticated={isAuthenticated}
            redirectUrl={dashboardUrl}
          >
            <ForgotPasswordView />
          </LoggedOutRoute>
        }
      />
      <Route
        path="/listings/:listingId"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <PropertyDetailsView />
          </UnprotectedRoute>
        }
        loader={listingLoader}
      />
      <Route
        path="/insights-stories"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <BlogsView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/insights-stories/:slug"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <BlogView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/property-search/:searchText"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <PropertySearchView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/property-search"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <PropertySearchView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/about"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <AboutView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/faqs"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <FaqView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/contact-us"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <ContactUsView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/our-team"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <OurTeamView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <PrivacyPolicyView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/terms-and-conditions"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <TermsAndConditionsView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/how-it-works"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <HowItWorksView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/refund-policy"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <RefundPolicyView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/data-consent"
        element={
          <UnprotectedRoute isAuthenticated={isAuthenticated}>
            <DataConsentView />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/purchase/request/:lid"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            section={USER_TYPES.BUYER}
          >
            <PropertyPurchaseView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase/invoice/:referenceId"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            section={USER_TYPES.BUYER}
          >
            <PurchaseInvoiceView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase/checkout/:lid"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            section={USER_TYPES.BUYER}
          >
            <PurchaseCheckoutView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase/account-setup/:lid"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            section={USER_TYPES.BUYER}
          >
            <PurchaseAccountSetupView />
          </ProtectedRoute>
        }
      />
      {/* <Route
        path="/dashboard"
        element={
          <ProtectedRoute
            section={USER_TYPES.BUYER}
            isAuthenticated={isAuthenticated}
          >
            <BuyerDashboardView />
          </ProtectedRoute>
        }
      /> */}
      {/* <Route
        path="/dashboard/sample-items"
        element={
          <ProtectedRoute
            section={USER_TYPES.BUYER}
            isAuthenticated={isAuthenticated}
          >
            <BuyerDashboardView />
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/dashboard/transactions"
        element={
          <ProtectedRoute
            section={USER_TYPES.BUYER}
            isAuthenticated={isAuthenticated}
          >
            <BuyerTransactionsView />
          </ProtectedRoute>
        }
      />{" "}
      <Route
        path="/dashboard/my-properties"
        element={
          <ProtectedRoute
            section={USER_TYPES.BUYER}
            isAuthenticated={isAuthenticated}
          >
            <BuyerPropertiesView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyer/profile"
        element={
          <ProtectedRoute
            section={USER_TYPES.BUYER}
            isAuthenticated={isAuthenticated}
          >
            <BuyerProfileView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/saved-properties"
        element={
          <ProtectedRoute
            section={USER_TYPES.BUYER}
            isAuthenticated={isAuthenticated}
          >
            <BuyerSavedItemsView />
          </ProtectedRoute>
        }
      />
    </>
  );
};

export default BuyerRoutes;
